import { useState, useEffect } from 'react'
import { getIdeas, createIdea, updateIdea } from '../services/ideas'
import { ApiErrorException } from '../services/client'
import type { Idea } from '../services/types'
import Modal from '../components/Modal'
import IdeaForm, { type FormData } from '../components/IdeaForm'
import { useToast } from '../components/Toast'

type StatusFilter = 'all' | 'open' | 'partial' | 'done' | 'abandoned'

const statusConfig = {
  open: { label: 'Open', class: 'status-open' },
  partial: { label: 'Partial', class: 'status-partial' },
  done: { label: 'Done', class: 'status-done' },
  abandoned: { label: 'Abandoned', class: 'status-abandoned' },
}

function QueueView() {
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null)
  const { showSuccess, showError } = useToast()

  useEffect(() => {
    loadIdeas()
  }, [statusFilter])

  const loadIdeas = async () => {
    setLoading(true)
    setError(null)
    try {
      const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined
      const response = await getIdeas(filters)
      const sorted = response.items.sort((a, b) => {
        if (a.rank !== b.rank) {
          return b.rank - a.rank
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      setIdeas(sorted)
    } catch (err) {
      console.error('Failed to load ideas:', err)
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Connection failed. Check network status.'
      setError(message)
      showError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateIdea = async (data: FormData) => {
    try {
      const payload: any = {
        name: data.name,
        rank: data.rank,
      }
      if (data.description) payload.description = data.description
      if (data.category) payload.category = data.category

      await createIdea(payload)
      setIsModalOpen(false)
      showSuccess('Idea initialized successfully')
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Initialization failed. Retry operation.'
      showError(message)
      throw err
    }
  }

  const handleUpdateIdea = async (data: FormData) => {
    if (!editingIdea) return
    try {
      const payload: any = {
        name: data.name,
        rank: data.rank,
        status: data.status,
      }
      if (data.description) payload.description = data.description
      if (data.category) payload.category = data.category

      await updateIdea(editingIdea.id, payload)
      setEditingIdea(null)
      showSuccess('Idea updated successfully')
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Update failed. Retry operation.'
      showError(message)
      throw err
    }
  }

  const handleEditClick = (idea: Idea) => {
    setEditingIdea(idea)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingIdea(null)
  }

  const tabs: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'partial', label: 'Partial' },
    { key: 'done', label: 'Done' },
    { key: 'abandoned', label: 'Abandoned' },
  ]

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-wider mb-1">Priority Queue</h1>
          <p className="text-[var(--chrome-300)] text-sm">
            {ideas.length} {ideas.length === 1 ? 'node' : 'nodes'} in the datastream
          </p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <span className="text-lg leading-none">+</span>
          <span>New Idea</span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="glass-panel p-1 mb-8 inline-flex gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-all duration-300 rounded ${
              statusFilter === tab.key
                ? 'bg-[var(--energy-cyan)]/10 text-[var(--energy-cyan)] shadow-[inset_0_0_20px_rgba(0,240,255,0.1)]'
                : 'text-[var(--chrome-300)] hover:text-[var(--chrome-100)] hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="loading-spinner mb-4" />
          <p className="text-[var(--chrome-300)] text-sm tracking-wider uppercase">
            Loading datastream...
          </p>
        </div>
      ) : error ? (
        <div className="glass-panel p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-2 border-[var(--alert-coral)] rotate-45 flex items-center justify-center">
            <span className="text-[var(--alert-coral)] text-xl -rotate-45">!</span>
          </div>
          <p className="text-[var(--alert-coral)] mb-4">{error}</p>
          <button onClick={loadIdeas} className="btn-ghost">
            Retry Connection
          </button>
        </div>
      ) : ideas.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-2 border-[var(--chrome-400)] rotate-45 opacity-30" />
            <div className="absolute inset-4 border border-[var(--chrome-400)] rotate-45 opacity-20" />
          </div>
          <h3 className="text-xl text-[var(--chrome-200)] mb-2">No Ideas Found</h3>
          <p className="text-[var(--chrome-400)] text-sm mb-6">
            Initialize your first idea to begin building the ideaverse
          </p>
          <button onClick={() => setIsModalOpen(true)} className="btn-primary">
            Create First Idea
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {ideas.map((idea, index) => (
            <div
              key={idea.id}
              onClick={() => handleEditClick(idea)}
              className="crystal-card cursor-pointer p-5 group"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Content Grid */}
              <div className="flex items-start gap-4">
                {/* Rank Indicator */}
                <div className="flex-shrink-0">
                  <div className="rank-indicator text-lg">
                    {idea.rank}
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="text-lg font-semibold text-[var(--chrome-100)] group-hover:text-[var(--energy-cyan)] transition-colors truncate">
                      {idea.name}
                    </h3>
                    <span className={`status-badge flex-shrink-0 ${statusConfig[idea.status].class}`}>
                      {statusConfig[idea.status].label}
                    </span>
                  </div>

                  {idea.description && (
                    <p className="text-[var(--chrome-300)] text-sm mb-3 line-clamp-2">
                      {idea.description}
                    </p>
                  )}

                  {/* Meta */}
                  <div className="flex items-center gap-3">
                    {idea.category && (
                      <span className="category-tag">
                        {idea.category}
                      </span>
                    )}
                    <span className="text-xs text-[var(--chrome-500)]">
                      {new Date(idea.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Arrow indicator */}
                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-[var(--energy-cyan)]">
                  →
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Initialize New Idea"
      >
        <IdeaForm
          mode="create"
          onSubmit={handleCreateIdea}
          onCancel={handleCloseModal}
        />
      </Modal>

      <Modal
        isOpen={!!editingIdea}
        onClose={handleCloseModal}
        title="Modify Idea Node"
      >
        {editingIdea && (
          <IdeaForm
            mode="edit"
            initialData={editingIdea}
            onSubmit={handleUpdateIdea}
            onCancel={handleCloseModal}
          />
        )}
      </Modal>
    </div>
  )
}

export default QueueView
